import {
    BreachScreenType,
    CartEvent,
    SswPackage,
    isSswPackage,
} from '@news-mono/web-common'
import { getAttribution } from '../../errors/ErrorPage'
import { Theme } from '@emotion/react'
import {
    SubscriptionPackageOptions,
    SubscriptionPriceObject,
} from '../../user-registration/breach/helpers/BreachWallTypes'

export const TEACHERS_OFFER = 'teachers-offer'
export const UNIVERSITY_OFFER = 'university-offer'

//The default quantity used for all GA4 events
export const DEFAULT_CART_QUANTITY = 4

// The quantity used for annual subscriptions on GA4 events
const ANNUAL_CART_QUANTITY = 52

export type SwgPackageOptions = {
    quantity?: number
    promoPrice?: string
    originalPrice?: string
    modifier?: string
    discount?: number
    coupon?: string
    varient?: 'swg' | 'native'
}

type SubscriptionPackage = {
    price: SubscriptionPriceObject
    package: SubscriptionPackageOptions
}

/**
 * This will generate a new cart event payload, which is used to send to the GTM layer.
 *
 * @param packageInfo - the package to remove from cart, this can be obtained from a getSswPackageByName(string)
 * @param theme - the theme to apply, used for brand identification
 * @param options - any page price options, including quantity
 * @param breachScreenType - only when passed through is this provided
 */
export function getCartPayload(
    packageInfo: SubscriptionPackage | SswPackage | undefined,
    mastheadTheme: Theme | string,
    options?: SwgPackageOptions,
    breachScreenType?: BreachScreenType,
): CartEvent['payload'] | undefined {
    if (!packageInfo) {
        return undefined
    }

    const item_brand =
        typeof mastheadTheme === 'string'
            ? mastheadTheme
            : getAttribution(mastheadTheme.kind, mastheadTheme.section) ||
              'The West Australian'

    let item_id = undefined
    let item_name = undefined
    let price = undefined
    let promoPrice = undefined
    let discount = undefined
    let coupon = undefined

    if (isSswPackage(packageInfo)) {
        // Check if it's an SSW package, if so set variables
        const pkg = packageInfo as SswPackage

        item_id = pkg.stdRateCode
        item_name = pkg.title
        price = pkg.weeklyPrice
        promoPrice =
            options?.promoPrice === undefined
                ? 0
                : getWeeklyPrice(options?.modifier)
        discount = options?.discount || 0
        coupon = options?.coupon || ''
    } else {
        const pkg = packageInfo as SubscriptionPackage

        item_id = pkg.price.rateCode
        price = pkg.price.weeklyPrice
        promoPrice = pkg.price.promoPrice || 0
        discount = pkg.price.discount || 0
        item_name = pkg.package.heading
        coupon = pkg.price.offerCode || ''
    }

    // Is the selected rateCode an annual package?
    const isAnnualPackage = ['WST_DIGI_12MTH25OFF'].includes(item_id)
    // Use the options if provided, otherwise use defaults based on rateCode (item_id)
    const quantity =
        options?.quantity || isAnnualPackage
            ? ANNUAL_CART_QUANTITY
            : DEFAULT_CART_QUANTITY

    // now using the set values, create a payload
    return {
        currency: 'AUD',
        value: (promoPrice > 0 ? promoPrice : price) * quantity,
        items: [
            {
                item_id: item_id,
                item_brand: item_brand,
                item_name: item_name,
                price: price,
                promo_price: promoPrice,
                discount: discount,
                coupon: coupon,
                quantity: quantity,
                item_variant: options?.varient || 'native',
            },
        ],
        breachScreenType: breachScreenType || undefined,
    }
}

// This function only relates to SwG products that have a landing page on news-mono
function getWeeklyPrice(modifier?: string): number {
    switch (modifier) {
        case UNIVERSITY_OFFER:
            return 0.25
        case TEACHERS_OFFER:
            return 0
        default:
            break
    }
    return 0
}
